import React, { useCallback, useEffect, useMemo, useRef } from 'react'

import { useVirtualizer } from '@tanstack/react-virtual'
import dynamic from 'next/dynamic'
import { BsFolderFill, BsGlobe2 } from 'react-icons/bs'
import { HiOutlineTag } from 'react-icons/hi2'
import { LuChevronRight, LuFolderOpen, LuLoader2, LuX } from 'react-icons/lu'
import { toast } from 'sonner'
import { twMerge } from 'tailwind-merge'

import { CompactThumbnail } from '@/components/common/Attachment/Thumbnail'
import Button from '@/components/common/Button'
import Checkbox from '@/components/common/Checkbox'
import {
  Combobox,
  ComboboxTrigger,
} from '@/components/common/ComboboxV2/Combobox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/common/Dialog'
import PopoverV2 from '@/components/common/PopoverV2'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import {
  SharepointPickerProvider,
  usePickerBreadcrumbs,
  usePickerClearCurrentDriveSelection,
  usePickerClearSelection,
  usePickerCurrentDriveId,
  usePickerCurrentFolderId,
  usePickerCurrentSite,
  usePickerCurrentSiteId,
  usePickerCurrentSitePath,
  usePickerIsSubmitting,
  usePickerNativeFallbackOpen,
  usePickerNavigateToBreadcrumb,
  usePickerNavigateToFolder,
  usePickerRemoveSelectedItem,
  usePickerReset,
  usePickerSelectDriveInSite,
  usePickerSelectedCount,
  usePickerSelectedEntries,
  usePickerSelectedItems,
  usePickerSelectedTagIds,
  usePickerSetCurrentSitePath,
  usePickerSetNativeFallbackOpen,
  usePickerSetTagIds,
  usePickerStoreApi,
  usePickerToggleSelection,
} from '@/components/integrations/SharepointFilePicker/context'
import {
  EMPTY_BREADCRUMBS,
  EMPTY_DRIVES,
  EMPTY_DRIVE_ITEMS,
  EMPTY_INITIAL_ITEMS,
  EMPTY_INITIAL_TAG_IDS,
  type PortableSharepointPickerBreadcrumb,
  getRelativeDate,
  getSelectionKey,
  isSharepointItemAccepted,
  selectDrivesWithAccess,
  selectSortedDriveItems,
} from '@/components/integrations/SharepointFilePicker/model'
import { SharepointSiteDriveTree } from '@/components/integrations/SharepointFilePicker/SiteDriveTree'
import type { SelectionRules } from '@/components/integrations/SharepointFilePicker/store'
import type {
  SharepointFilePickerProps,
  SharepointPickerItem,
} from '@/components/integrations/SharepointFilePicker/types'
import { Tag } from '@/components/tags/Tag'
import { TagComboboxContent } from '@/components/tags/TagCombobox'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import type {
  SharePointDriveItem,
  SharePointSite,
} from '@/hooks/sharepoint/types'
import { useSharepointDriveChildren } from '@/hooks/sharepoint/useSharepointDriveChildren'
import { useSharepointDrives } from '@/hooks/sharepoint/useSharepointDrives'

import type { UUID } from '@/types/Id'
import type { TagType } from '@/types/Tag'

const SharepointNativeFilePicker = dynamic(
  () =>
    import('@/components/integrations/SharepointFilePicker/NativeFilePicker').then(
      (mod) => mod.SharepointNativeFilePicker,
    ),
  { ssr: false },
)

// ─── Breadcrumbs ─────────────────────────────────────────────────────────────

interface SharepointPickerBreadcrumbButtonProps {
  breadcrumb: PortableSharepointPickerBreadcrumb
  index: number
  isLast: boolean
  onSelectBreadcrumb: (index: number) => void
}

const SharepointPickerBreadcrumbButton: React.FC<
  SharepointPickerBreadcrumbButtonProps
> = ({ breadcrumb, index, isLast, onSelectBreadcrumb }) => {
  const handleClick = useCallback(() => {
    onSelectBreadcrumb(index)
  }, [index, onSelectBreadcrumb])

  return (
    <button
      type="button"
      disabled={isLast}
      onClick={handleClick}
      className={twMerge(
        'truncate rounded-md px-2 py-0.5 text-[13px] transition-colors',
        isLast
          ? 'cursor-default font-medium text-primary-900 dark:text-primary-100'
          : 'text-primary-500 hover:bg-primary-100 hover:text-primary-800 dark:text-primary-400 dark:hover:bg-primary-800 dark:hover:text-primary-100',
      )}
    >
      {breadcrumb.name}
    </button>
  )
}

interface SharepointPickerBreadcrumbsProps {
  breadcrumbs: PortableSharepointPickerBreadcrumb[]
  maxVisible?: number
  onSelectBreadcrumb: (index: number) => void
}

const MAX_VISIBLE_BREADCRUMBS = 4

const SharepointPickerBreadcrumbs: React.FC<
  SharepointPickerBreadcrumbsProps
> = ({
  breadcrumbs,
  maxVisible = MAX_VISIBLE_BREADCRUMBS,
  onSelectBreadcrumb,
}) => {
  const max = Math.max(maxVisible, 2)
  const shouldSlice = breadcrumbs.length > max

  const displayItems = useMemo(() => {
    if (!shouldSlice) {
      return breadcrumbs.map((crumb, idx) => ({
        type: 'breadcrumb' as const,
        crumb,
        actualIndex: idx,
      }))
    }

    const tailCount = max - 1
    const tailStartIndex = breadcrumbs.length - tailCount
    const items: Array<
      | {
          type: 'breadcrumb'
          crumb: PortableSharepointPickerBreadcrumb
          actualIndex: number
        }
      | {
          type: 'ellipsis'
          hiddenItems: Array<{
            crumb: PortableSharepointPickerBreadcrumb
            actualIndex: number
          }>
        }
    > = []

    // First breadcrumb
    items.push({ type: 'breadcrumb', crumb: breadcrumbs[0], actualIndex: 0 })

    // Ellipsis with hidden items
    items.push({
      type: 'ellipsis',
      hiddenItems: breadcrumbs
        .slice(1, tailStartIndex)
        .map((crumb, idx) => ({ crumb, actualIndex: idx + 1 })),
    })

    // Last N breadcrumbs
    for (let i = tailStartIndex; i < breadcrumbs.length; i++) {
      items.push({ type: 'breadcrumb', crumb: breadcrumbs[i], actualIndex: i })
    }

    return items
  }, [breadcrumbs, shouldSlice, max])

  return (
    <nav className="flex items-center gap-0.5 overflow-x-auto">
      {displayItems.map((item, idx) => {
        if (item.type === 'ellipsis') {
          return (
            <React.Fragment key="ellipsis">
              <LuChevronRight
                size={13}
                className="shrink-0 text-primary-400 dark:text-primary-500"
              />
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="rounded-md px-2 py-0.5 text-[13px] text-primary-400 transition-colors hover:bg-primary-100 hover:text-primary-600 dark:text-primary-500 dark:hover:bg-primary-800 dark:hover:text-primary-300"
                  >
                    …
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="start"
                  sideOffset={4}
                  inverseColors
                  className="w-auto min-w-[160px] max-w-[260px] p-1"
                >
                  {item.hiddenItems.map(({ crumb, actualIndex }) => (
                    <button
                      key={crumb.id}
                      type="button"
                      onClick={() => onSelectBreadcrumb(actualIndex)}
                      className="flex w-full items-center gap-2 truncate rounded px-2 py-1.5 text-left text-[13px] text-primary-700 transition-colors hover:bg-primary-100 dark:text-primary-200 dark:hover:bg-primary-800"
                    >
                      <LuChevronRight
                        size={11}
                        className="shrink-0 text-primary-400"
                      />
                      <span className="truncate">{crumb.name}</span>
                    </button>
                  ))}
                </TooltipContent>
              </Tooltip>
            </React.Fragment>
          )
        }

        const isLast = idx === displayItems.length - 1
        return (
          <React.Fragment key={`${item.crumb.type}:${item.crumb.id}`}>
            {idx > 0 ? (
              <LuChevronRight
                size={13}
                className="shrink-0 text-primary-400 dark:text-primary-500"
              />
            ) : null}
            <SharepointPickerBreadcrumbButton
              breadcrumb={item.crumb}
              index={item.actualIndex}
              isLast={isLast}
              onSelectBreadcrumb={onSelectBreadcrumb}
            />
          </React.Fragment>
        )
      })}
    </nav>
  )
}

// ─── File row ─────────────────────────────────────────────────────────────────

const noop = () => {}

interface SharepointPickerRowProps {
  disabled?: boolean
  index: number
  isSelected: boolean
  item: SharePointDriveItem
  onNavigateFolder: (item: SharePointDriveItem) => void
  onToggleSelection: (
    item: SharePointDriveItem,
    index: number,
    shiftKey: boolean,
  ) => void
  selectionPolicy: NonNullable<SharepointFilePickerProps['selectionPolicy']>
}

const SharepointPickerRow: React.FC<SharepointPickerRowProps> = ({
  disabled = false,
  index,
  isSelected,
  item,
  onNavigateFolder,
  onToggleSelection,
  selectionPolicy,
}) => {
  const showCheckbox =
    !item.is_folder || selectionPolicy === 'files_and_folders'

  const handleRowClick = useCallback(
    (e: React.MouseEvent) => {
      if (item.is_folder) {
        onNavigateFolder(item)
        return
      }
      if (!disabled) {
        onToggleSelection(item, index, e.shiftKey)
      }
    },
    [disabled, index, item, onNavigateFolder, onToggleSelection],
  )

  const handleCheckboxClick = useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      e.stopPropagation()
      if (!disabled) {
        onToggleSelection(item, index, e.shiftKey)
      }
    },
    [disabled, index, item, onToggleSelection],
  )

  return (
    <div
      className={twMerge(
        'group flex h-10 items-center gap-2 border-b border-primary-100 px-3 transition-colors dark:border-primary-800/60',
        isSelected
          ? 'bg-blue-50/70 dark:bg-blue-900/20'
          : 'hover:bg-primary-50 dark:hover:bg-primary-900/40',
        disabled && 'opacity-50',
      )}
    >
      {/* Checkbox column */}
      <div className="flex w-5 shrink-0 items-center justify-center">
        {showCheckbox ? (
          <Checkbox
            checked={isSelected}
            disabled={disabled}
            onChange={noop}
            onClick={handleCheckboxClick}
            childrenOnly
          />
        ) : null}
      </div>

      {/* Name column */}
      <button
        type="button"
        // Folders stay open-able whatever is being picked, so only a disabled
        // file is actually dead.
        disabled={disabled && !item.is_folder}
        onClick={handleRowClick}
        className="flex min-w-0 flex-1 items-center gap-2.5 py-1 text-left"
      >
        {item.is_folder ? (
          <BsFolderFill size={16} className="shrink-0 text-amber-500" />
        ) : (
          <CompactThumbnail name={item.name} size={16} />
        )}
        <span
          className={twMerge(
            'flex-1 truncate text-[13px] underline-offset-2',
            !disabled && 'hover:underline',
            isSelected
              ? 'text-blue-800 dark:text-blue-200'
              : 'text-primary-900 dark:text-primary-100',
          )}
        >
          {item.name}
        </span>
        {item.is_folder ? (
          <LuChevronRight
            size={13}
            className="shrink-0 text-primary-300 opacity-0 transition-opacity group-hover:opacity-100 dark:text-primary-600"
          />
        ) : null}
      </button>

      {/* Modified column */}
      <div className="hidden w-32 shrink-0 truncate text-right text-[12px] text-primary-400 sm:block dark:text-primary-500">
        {item.is_folder
          ? typeof item.child_count === 'number'
            ? `${item.child_count} item${item.child_count === 1 ? '' : 's'}`
            : ''
          : (getRelativeDate(item.last_modified) ?? '')}
      </div>
    </div>
  )
}

// ─── Empty state ──────────────────────────────────────────────────────────────

interface SharepointPickerEmptyStateProps {
  description: React.ReactNode
  icon?: React.ReactNode
  title: React.ReactNode
}

const SharepointPickerEmptyState: React.FC<SharepointPickerEmptyStateProps> = ({
  description,
  icon,
  title,
}) => {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-8 py-16 text-center">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100/80 text-primary-400 dark:bg-primary-800/60 dark:text-primary-500">
        {icon ?? <LuFolderOpen size={20} />}
      </div>
      <div>
        <p className="text-[14px] font-medium text-primary-700 dark:text-primary-300">
          {title}
        </p>
        <div className="mt-1 text-[13px] leading-relaxed text-primary-500 dark:text-primary-400">
          {description}
        </div>
      </div>
    </div>
  )
}

// ─── Selected items popover ──────────────────────────────────────────────────

const SELECTED_ROW_HEIGHT = 30

interface SelectedItemsListProps {
  items: Array<[string, SharepointPickerItem]>
  onClearAll: () => void
  onRemove: (key: string) => void
  totalCount: number
}

const SelectedItemsList: React.FC<SelectedItemsListProps> = ({
  items,
  onClearAll,
  onRemove,
  totalCount,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => SELECTED_ROW_HEIGHT,
    overscan: 10,
  })

  return (
    <>
      <div className="flex items-center justify-between border-b border-primary-100 px-3 py-2 dark:border-primary-800">
        <p className="text-[13px] font-medium text-primary-900 dark:text-primary-100">
          {totalCount} selected
        </p>
        <button
          type="button"
          onClick={onClearAll}
          className="text-[12px] text-primary-400 transition-colors hover:text-primary-700 dark:text-primary-500 dark:hover:text-primary-300"
        >
          Clear all
        </button>
      </div>
      <div ref={scrollRef} className="max-h-64 overflow-y-auto">
        <div
          className="relative w-full"
          style={{ height: virtualizer.getTotalSize() }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const entry = items[virtualRow.index]
            if (!entry) return null
            const [key, item] = entry
            return (
              <div
                key={key}
                className="group absolute left-0 flex w-full items-center gap-2 px-3"
                style={{
                  height: virtualRow.size,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {item.is_folder ? (
                  <BsFolderFill size={13} className="shrink-0 text-amber-500" />
                ) : (
                  <CompactThumbnail name={item.name ?? ''} size={13} />
                )}
                <span className="min-w-0 flex-1 truncate text-[12px] text-primary-700 dark:text-primary-300">
                  {item.name ?? 'Untitled'}
                </span>
                <button
                  type="button"
                  onClick={() => onRemove(key)}
                  className="shrink-0 rounded p-0.5 text-primary-300 opacity-0 transition-opacity hover:text-primary-600 group-hover:opacity-100 dark:text-primary-600 dark:hover:text-primary-300"
                >
                  <LuX size={12} />
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}

interface SharepointPickerTagSectionProps {
  selectedTagIds: UUID[]
  tagType: TagType
  onSelectedTagIdsChange: (tagIds: UUID[]) => void
}

/** Tag chips bar — renders above the footer when tags are selected. */
const SharepointPickerTagBar: React.FC<
  Omit<SharepointPickerTagSectionProps, 'tagType'>
> = ({ selectedTagIds, onSelectedTagIdsChange }) => {
  const handleRemoveTag = useCallback(
    (tagId: UUID) => () => {
      onSelectedTagIdsChange(selectedTagIds.filter((id) => id !== tagId))
    },
    [onSelectedTagIdsChange, selectedTagIds],
  )

  const handleClearAll = useCallback(() => {
    onSelectedTagIdsChange([] as UUID[])
  }, [onSelectedTagIdsChange])

  if (selectedTagIds.length === 0) return null

  return (
    <div className="flex items-center gap-2 border-t border-primary-200 bg-primary-50/60 px-5 py-2 dark:border-primary-800 dark:bg-primary-950/40">
      <span className="shrink-0 text-[12px] font-medium text-primary-500 dark:text-primary-400">
        Tags:
      </span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
        {selectedTagIds.map((tagId) => (
          <Tag key={tagId} tagId={tagId} onDelete={handleRemoveTag(tagId)} />
        ))}
      </div>
      <button
        type="button"
        onClick={handleClearAll}
        className="shrink-0 text-[12px] font-medium text-primary-400 transition-colors hover:text-primary-700 dark:text-primary-500 dark:hover:text-primary-200"
      >
        Clear all
      </button>
    </div>
  )
}

/** Tag combobox trigger — lives inside the footer, never shifts. */
const SharepointPickerTagTrigger: React.FC<SharepointPickerTagSectionProps> = ({
  selectedTagIds,
  tagType,
  onSelectedTagIdsChange,
}) => {
  const [open, setOpen] = React.useState(false)

  const handleTagSelectionChange = useCallback(
    (value: string[] | null) => {
      onSelectedTagIdsChange((value ?? []) as UUID[])
    },
    [onSelectedTagIdsChange],
  )

  return (
    <Combobox open={open} onOpenChange={setOpen}>
      <ComboboxTrigger asChild>
        <button
          type="button"
          className={twMerge(
            'inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1 text-[13px] font-medium transition-colors',
            'border-primary-200 bg-white text-primary-700 shadow-sm hover:bg-primary-50 hover:border-primary-300',
            'dark:border-primary-700 dark:bg-primary-900 dark:text-primary-200 dark:hover:bg-primary-800 dark:hover:border-primary-600',
          )}
        >
          <HiOutlineTag size={14} />
          {selectedTagIds.length > 0 ? 'Edit tags' : 'Add tags'}
        </button>
      </ComboboxTrigger>
      {open && (
        <TagComboboxContent
          value={selectedTagIds}
          multiple
          onChange={handleTagSelectionChange}
          comboboxContentProps={{ align: 'start', side: 'top' }}
          tagType={tagType}
        />
      )}
    </Combobox>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export const SharepointFilePicker: React.FC<SharepointFilePickerProps> = ({
  initialItems,
  initialTagIds,
  ...props
}) => {
  const normalizedInitialItems = initialItems ?? EMPTY_INITIAL_ITEMS
  const normalizedInitialTagIds = initialTagIds ?? EMPTY_INITIAL_TAG_IDS

  return (
    <SharepointPickerProvider
      initialItems={normalizedInitialItems}
      initialTagIds={normalizedInitialTagIds}
    >
      <SharepointFilePickerInner
        {...props}
        initialItems={normalizedInitialItems}
        initialTagIds={normalizedInitialTagIds}
      />
    </SharepointPickerProvider>
  )
}

// ─── Inner component (consumes store via context hooks) ──────────────────────

interface SharepointFilePickerInnerProps extends Omit<
  SharepointFilePickerProps,
  'initialItems' | 'initialTagIds'
> {
  initialItems: SharepointPickerItem[]
  initialTagIds: UUID[]
}

const SharepointFilePickerInner: React.FC<SharepointFilePickerInnerProps> = ({
  accept = [],
  confirmLabel = 'Add selected',
  description = 'Choose files from SharePoint.',
  initialItems,
  initialTagIds,
  isOpen,
  multiple = true,
  nativeFallbackEnabled = true,
  nativeFallbackLabel = "Not seeing a file? Try Microsoft's native file picker.",
  onClose,
  onSubmit,
  pendingLabel = 'Adding…',
  selectionPolicy = 'files',
  tagType,
  title = 'Import from SharePoint',
}) => {
  const { activeOrgId } = useActiveOrgId()
  const store = usePickerStoreApi()

  // ── Store selectors ──

  const currentFolderId = usePickerCurrentFolderId()
  const breadcrumbs = usePickerBreadcrumbs()
  const nativeFallbackOpen = usePickerNativeFallbackOpen()
  const isSubmitting = usePickerIsSubmitting()
  const selectedItems = usePickerSelectedItems()
  const selectedTagIds = usePickerSelectedTagIds()
  const selectedCount = usePickerSelectedCount()
  const selectedEntries = usePickerSelectedEntries()
  const currentSite = usePickerCurrentSite()
  const currentSitePath = usePickerCurrentSitePath()
  const currentSiteId = usePickerCurrentSiteId()
  const currentDriveId = usePickerCurrentDriveId()

  // ── Store actions ──

  const selectDriveInSite = usePickerSelectDriveInSite()
  const navigateToFolder = usePickerNavigateToFolder()
  const navigateToBreadcrumb = usePickerNavigateToBreadcrumb()
  const toggleSelection = usePickerToggleSelection()
  const removeSelectedItem = usePickerRemoveSelectedItem()
  const clearSelection = usePickerClearSelection()
  const clearCurrentDriveSelection = usePickerClearCurrentDriveSelection()
  const setTagIds = usePickerSetTagIds()
  const setCurrentSitePath = usePickerSetCurrentSitePath()
  const setNativeFallbackOpen = usePickerSetNativeFallbackOpen()
  const reset = usePickerReset()

  // ── Reset on open ──

  const wasOpenRef = useRef(false)
  const latestInitialItemsRef = useRef(initialItems)
  const latestInitialTagIdsRef = useRef(initialTagIds)
  latestInitialItemsRef.current = initialItems
  latestInitialTagIdsRef.current = initialTagIds

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false
      setNativeFallbackOpen(false)
      return
    }
    if (wasOpenRef.current) return
    wasOpenRef.current = true
    reset(latestInitialItemsRef.current, latestInitialTagIdsRef.current)
  }, [isOpen, setNativeFallbackOpen, reset])

  const { data: currentSiteDrivesData } = useSharepointDrives({
    orgId: activeOrgId,
    siteId: currentSiteId,
    enabled: !!activeOrgId && !!currentSiteId && isOpen,
    select: selectDrivesWithAccess,
  })

  const currentSiteDrives = currentSiteDrivesData?.drives ?? EMPTY_DRIVES
  const hasCurrentDrive =
    !!currentDriveId &&
    currentSiteDrives.some((drive) => drive.id === currentDriveId)
  const shouldClearUnavailableDrive =
    !!currentDriveId && currentSiteDrivesData != null && !hasCurrentDrive

  useEffect(() => {
    if (!shouldClearUnavailableDrive) {
      return
    }

    clearCurrentDriveSelection()
  }, [clearCurrentDriveSelection, shouldClearUnavailableDrive])

  const effectiveCurrentDriveId =
    currentDriveId && (currentSiteDrivesData == null || hasCurrentDrive)
      ? currentDriveId
      : ''
  const effectiveCurrentFolderId = effectiveCurrentDriveId
    ? currentFolderId
    : null
  const effectiveBreadcrumbs = effectiveCurrentDriveId
    ? breadcrumbs
    : EMPTY_BREADCRUMBS

  const handleCommitBrowsePath = useCallback(
    (sitePath: SharePointSite[]) => {
      setCurrentSitePath(sitePath)
    },
    [setCurrentSitePath],
  )

  const {
    data: items = EMPTY_DRIVE_ITEMS,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading: isItemsLoading,
    refetch,
  } = useSharepointDriveChildren<SharePointDriveItem[]>({
    orgId: activeOrgId,
    driveId: effectiveCurrentDriveId,
    itemId: effectiveCurrentFolderId ?? undefined,
    enabled: !!activeOrgId && !!effectiveCurrentDriveId,
    select: selectSortedDriveItems,
  })

  // ── Infinite scroll sentinel ──

  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage()
        }
      },
      { threshold: 0 },
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  // ── Handlers ──

  const selectionRules = useMemo<SelectionRules>(
    () => ({ accept, multiple, selectionPolicy }),
    [accept, multiple, selectionPolicy],
  )

  const handleToggleSelection = useCallback(
    (item: SharePointDriveItem, index: number, shiftKey: boolean) => {
      if (!effectiveCurrentDriveId) return
      toggleSelection(
        item,
        effectiveCurrentDriveId,
        index,
        shiftKey,
        items,
        selectionRules,
      )
    },
    [toggleSelection, effectiveCurrentDriveId, items, selectionRules],
  )

  const handleSubmit = useCallback(async () => {
    const state = store.getState()
    if (state.isSubmitting) return

    state.setIsSubmitting(true)
    const items = Object.values(state.selectedItems)
    try {
      const ok = await Promise.resolve(
        onSubmit({ items, tag_ids: state.selectedTagIds }),
      )
      if (ok) onClose()
    } finally {
      store.getState().setIsSubmitting(false)
    }
  }, [store, onClose, onSubmit])

  const handleNativeSubmit = useCallback(
    async (nativeItems: SharepointPickerItem[]) => {
      const state = store.getState()
      const toastId = toast.loading('Uploading files from SharePoint…')
      try {
        const ok = await Promise.resolve(
          onSubmit({ items: nativeItems, tag_ids: state.selectedTagIds }),
        )
        if (ok) {
          toast.success(
            `${nativeItems.length} file${nativeItems.length === 1 ? '' : 's'} uploaded.`,
            { id: toastId },
          )
          setNativeFallbackOpen(false)
          onClose()
        } else {
          toast.error('Upload failed. Please try again.', { id: toastId })
        }
        return ok
      } catch (error) {
        console.error('SharePoint native file picker upload failed:', error)
        toast.error('Upload failed. Please try again.', { id: toastId })
        return false
      }
    },
    [store, setNativeFallbackOpen, onClose, onSubmit],
  )

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open) onClose()
    },
    [onClose],
  )

  const handleRefetch = useCallback(() => {
    void refetch()
  }, [refetch])

  const handleSelectedTagIdsChange = useCallback(
    (tagIds: UUID[]) => setTagIds(tagIds),
    [setTagIds],
  )

  // ── Derived values ──

  const dialogOpen = isOpen && !nativeFallbackOpen

  const handleOpenNativeFallback = useCallback(() => {
    setNativeFallbackOpen(true)
  }, [setNativeFallbackOpen])

  const handleCloseNativeFallback = useCallback(() => {
    setNativeFallbackOpen(false)
  }, [setNativeFallbackOpen])

  return (
    <>
      <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent
          aria-describedby={undefined}
          className="flex h-[min(800px,87vh)] w-[min(1240px,96vw)] min-w-0 flex-col overflow-hidden p-0"
        >
          {/* ── Header ── */}
          <div className="shrink-0 border-b border-primary-200 px-5 py-3 dark:border-primary-800 sm:py-4">
            <div className="flex flex-col gap-1 pr-8 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
              <DialogHeader className="space-y-0.5 sm:space-y-1">
                <DialogTitle className="text-[15px] sm:text-base">
                  {title}
                </DialogTitle>
                <DialogDescription className="text-[12px] sm:text-[13px]">
                  {description}
                </DialogDescription>
              </DialogHeader>
              {nativeFallbackEnabled ? (
                <button
                  type="button"
                  onClick={handleOpenNativeFallback}
                  className="shrink-0 self-start text-xs text-primary-400 underline-offset-2 transition-colors hover:text-primary-600 hover:underline sm:mt-1 sm:text-[12px] dark:text-primary-500 dark:hover:text-primary-300"
                >
                  {nativeFallbackLabel}
                </button>
              ) : null}
            </div>
          </div>

          {/* ── Body: sidebar + file browser ── */}
          <div className="flex min-h-0 flex-1 overflow-hidden">
            {/* Left sidebar: sites & drives */}
            <div className="flex w-56 shrink-0 flex-col overflow-hidden border-r border-primary-200 bg-white sm:w-72 dark:border-primary-800 dark:bg-primary-900">
              <SharepointSiteDriveTree
                defaultBrowsePath={currentSitePath}
                defaultBrowseSite={currentSite}
                onCommitBrowsePath={handleCommitBrowsePath}
                onSelectDrive={selectDriveInSite}
                orgId={activeOrgId}
                scrollClassName="pt-0 pb-1.5"
                selectedDriveId={effectiveCurrentDriveId || undefined}
                selectedSiteId={currentSiteId || undefined}
              />
            </div>

            {/* Right: file browser */}
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white dark:bg-default-dark divide-y divide-primary-200 dark:divide-primary-800">
              {/* Breadcrumb bar */}
              <div className="flex h-10 shrink-0 items-center px-4">
                {effectiveBreadcrumbs.length > 0 ? (
                  <SharepointPickerBreadcrumbs
                    breadcrumbs={effectiveBreadcrumbs}
                    onSelectBreadcrumb={navigateToBreadcrumb}
                  />
                ) : (
                  <span className="text-[13px] text-primary-400 dark:text-primary-500">
                    Select a site and library to browse files
                  </span>
                )}
              </div>

              {/* Column headers */}
              {effectiveCurrentDriveId ? (
                <div className="flex h-8 shrink-0 items-center gap-2 border-b border-primary-100 px-3 dark:border-primary-800/60">
                  <div className="w-5 shrink-0" />
                  <div className="flex-1 text-xs font-medium text-primary-500 dark:text-primary-400">
                    Name
                  </div>
                  <div className="hidden w-32 shrink-0 sm:block" />
                </div>
              ) : null}

              {/* File list content */}
              <div className="min-h-0 flex-1 overflow-y-auto">
                {!effectiveCurrentDriveId ? (
                  <SharepointPickerEmptyState
                    icon={<BsGlobe2 size={16} />}
                    title="Select a library to browse files"
                    description="Choose a site on the left, then pick a document library."
                  />
                ) : isItemsLoading ? (
                  <div className="flex h-full items-center justify-center gap-2 text-[13px] text-primary-500 dark:text-primary-400">
                    <LuLoader2
                      size={15}
                      className="animate-spin text-[#0078d4]"
                    />
                    <span>Loading files…</span>
                  </div>
                ) : isError ? (
                  <SharepointPickerEmptyState
                    title="Unable to load files"
                    description={
                      <Button variant="outline-compact" onClick={handleRefetch}>
                        Try again
                      </Button>
                    }
                  />
                ) : items.length === 0 ? (
                  <SharepointPickerEmptyState
                    title="This folder is empty"
                    description="Open another folder or choose a different library."
                  />
                ) : (
                  <>
                    {items.map((item, index) => {
                      const selectionKey = getSelectionKey({
                        drive_id: effectiveCurrentDriveId,
                        drive_item_id: item.id,
                        is_folder: item.is_folder,
                        name: item.name,
                      })
                      return (
                        <SharepointPickerRow
                          key={selectionKey}
                          disabled={!isSharepointItemAccepted(item, accept)}
                          index={index}
                          isSelected={selectionKey in selectedItems}
                          item={item}
                          onNavigateFolder={navigateToFolder}
                          onToggleSelection={handleToggleSelection}
                          selectionPolicy={selectionPolicy}
                        />
                      )
                    })}
                    {/* Infinite scroll sentinel */}
                    <div ref={sentinelRef} className="h-px" />
                    {isFetchingNextPage ? (
                      <div className="flex items-center justify-center gap-2 py-3 text-[13px] text-primary-400">
                        <LuLoader2 size={13} className="animate-spin" />
                        <span>Loading more…</span>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ── Selected tags bar (above footer) ── */}
          {tagType ? (
            <SharepointPickerTagBar
              selectedTagIds={selectedTagIds}
              onSelectedTagIdsChange={handleSelectedTagIdsChange}
            />
          ) : null}

          {/* ── Footer ── */}
          <DialogFooter className="shrink-0 flex-wrap items-end justify-between gap-x-4 gap-y-2 border-t border-primary-200 px-5 py-3.5 dark:border-primary-800">
            {/* Left: tag trigger */}
            <div className="flex min-w-0 items-center">
              {tagType ? (
                <SharepointPickerTagTrigger
                  selectedTagIds={selectedTagIds}
                  tagType={tagType}
                  onSelectedTagIdsChange={handleSelectedTagIdsChange}
                />
              ) : null}
            </div>

            {/* Right: selection count + action buttons */}
            <div className="flex shrink-0 items-center gap-2 ml-auto">
              {selectedCount > 0 ? (
                <PopoverV2>
                  <PopoverV2.Trigger asChild>
                    <button
                      type="button"
                      className="mr-1 text-[13px] text-primary-600 underline decoration-primary-300 underline-offset-2 transition-colors hover:text-primary-900 dark:text-primary-400 dark:decoration-primary-600 dark:hover:text-primary-200"
                    >
                      {selectedCount} item
                      {selectedCount === 1 ? '' : 's'} selected
                    </button>
                  </PopoverV2.Trigger>
                  <PopoverV2.Content
                    side="top"
                    align="end"
                    className="w-80 p-0"
                  >
                    <SelectedItemsList
                      items={selectedEntries}
                      onClearAll={clearSelection}
                      onRemove={removeSelectedItem}
                      totalCount={selectedCount}
                    />
                  </PopoverV2.Content>
                </PopoverV2>
              ) : null}
              <Button variant="outline-compact" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="blue-compact"
                onClick={handleSubmit}
                disabled={selectedCount === 0 || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <LuLoader2 size={14} className="animate-spin" />
                    <span>{pendingLabel}</span>
                  </>
                ) : (
                  confirmLabel
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {nativeFallbackOpen ? (
        <SharepointNativeFilePicker
          accept={accept}
          isOpen={nativeFallbackOpen}
          multiple={multiple}
          onClose={handleCloseNativeFallback}
          onSubmit={handleNativeSubmit}
        />
      ) : null}
    </>
  )
}
